import { RpcConfig, NetworkType } from './types/rpc';
import { rpcManager } from '@/utils/rpcManager';

/**
 * IMPORTANT: We're now primarily using WalletConnect's infrastructure for network connections
 * and gas price estimations. These RPC endpoints are kept as fallbacks only and should
 * not be used for primary connections or gas estimations.
 * 
 * The token creation page and other components should use the WalletConnect infrastructure
 * via hooks like usePublicClient() and useWalletConnectGas() instead of direct RPC calls.
 */

// Simplified RPC endpoints focusing on most reliable providers
const DEFAULT_RPCS = {
  bsc: [
    'https://bsc-dataseed.binance.org',
    'https://rpc.ankr.com/bsc',
    'https://bsc.publicnode.com'
  ],
  bscTestnet: [
    'https://data-seed-prebsc-1-s1.binance.org:8545',
    'https://bsc-testnet.publicnode.com'
  ],
  ethereum: [
    'https://eth.llamarpc.com',
    'https://rpc.ankr.com/eth'
  ],
  sepolia: [
    'https://rpc.sepolia.org',
    'https://rpc.ankr.com/eth_sepolia'
  ],
  avalanche: [
    'https://api.avax.network/ext/bc/C/rpc',
    'https://rpc.ankr.com/avalanche'
  ],
  fuji: [
    'https://api.avax-test.network/ext/bc/C/rpc',
    'https://rpc.ankr.com/avalanche_fuji'
  ],
  abstract: [
    'https://api.mainnet.abs.xyz',
    'https://rpc.ankr.com/abstract'
  ],
  abstractTestnet: [
    'https://api.testnet.abs.xyz',
    'https://rpc.ankr.com/abstract_testnet'
  ],
  base: [
    'https://mainnet.base.org',
    'https://base.llamarpc.com',
    'https://base.publicnode.com'
  ],
  baseSepolia: [
    'https://sepolia.base.org',
    'https://base-sepolia.publicnode.com'
  ],
  optimism: [
    'https://mainnet.optimism.io',
    'https://optimism.llamarpc.com',
    'https://optimism.publicnode.com'
  ],
  optimismSepolia: [
    'https://sepolia.optimism.io',
    'https://optimism-sepolia.publicnode.com'
  ],
  arbitrum: [
    'https://arb1.arbitrum.io/rpc',
    'https://arbitrum.llamarpc.com',
    'https://arbitrum.publicnode.com'
  ],
  arbitrumSepolia: [
    'https://sepolia-rollup.arbitrum.io/rpc',
    'https://arbitrum-sepolia.publicnode.com'
  ],
  polygon: [
    'https://polygon-rpc.com',
    'https://rpc.ankr.com/polygon',
    'https://polygon.publicnode.com'
  ],
  polygonAmoy: [
    'https://rpc-amoy.polygon.technology',
    'https://polygon-amoy.publicnode.com'
  ],
  sei: [
    'https://sei-mainnet.g.alchemy.com/v2/fTz24O1l0KaG3202Zf9qE',
    'https://evm-rpc.sei-apis.com',
    'https://sei-evm.publicnode.com'
  ],
  seiTestnet: [
    'https://evm-rpc.arctic-1.seinetwork.io',
    'https://evm-rpc-testnet.sei-apis.com'
  ]
};

// Simplified config without regional complexity
// Note: These are only used as fallbacks now, WalletConnect is the primary provider
const config: Record<NetworkType, RpcConfig> = {
  bsc: {
    primary: [
      import.meta.env.VITE_BSC_MAINNET_RPC,
      DEFAULT_RPCS.bsc[0]
    ].filter(Boolean),
    backup: DEFAULT_RPCS.bsc.slice(1)
  },
  bscTestnet: {
    primary: [
      import.meta.env.VITE_BSC_TESTNET_RPC,
      DEFAULT_RPCS.bscTestnet[0]
    ].filter(Boolean),
    backup: DEFAULT_RPCS.bscTestnet.slice(1)
  },
  ethereum: {
    primary: [
      import.meta.env.VITE_ETH_MAINNET_RPC,
      DEFAULT_RPCS.ethereum[0]
    ].filter(Boolean),
    backup: DEFAULT_RPCS.ethereum.slice(1)
  },
  sepolia: {
    primary: [
      import.meta.env.VITE_SEPOLIA_RPC,
      DEFAULT_RPCS.sepolia[0]
    ].filter(Boolean),
    backup: DEFAULT_RPCS.sepolia.slice(1)
  },
  avalanche: {
    primary: [
      import.meta.env.VITE_AVALANCHE_MAINNET_RPC,
      DEFAULT_RPCS.avalanche[0]
    ].filter(Boolean),
    backup: DEFAULT_RPCS.avalanche.slice(1)
  },
  fuji: {
    primary: [
      import.meta.env.VITE_AVALANCHE_TESTNET_RPC,
      DEFAULT_RPCS.fuji[0]
    ].filter(Boolean),
    backup: DEFAULT_RPCS.fuji.slice(1)
  },
  abstract: {
    primary: [
      import.meta.env.VITE_ABSTRACT_MAINNET_RPC,
      DEFAULT_RPCS.abstract[0]
    ].filter(Boolean),
    backup: DEFAULT_RPCS.abstract.slice(1)
  },
  abstractTestnet: {
    primary: [
      import.meta.env.VITE_ABSTRACT_TESTNET_RPC,
      DEFAULT_RPCS.abstractTestnet[0]
    ].filter(Boolean),
    backup: DEFAULT_RPCS.abstractTestnet.slice(1)
  },
  base: {
    primary: [
      import.meta.env.VITE_BASE_MAINNET_RPC,
      DEFAULT_RPCS.base[0]
    ].filter(Boolean),
    backup: DEFAULT_RPCS.base.slice(1)
  },
  baseSepolia: {
    primary: [
      import.meta.env.VITE_BASE_SEPOLIA_RPC,
      DEFAULT_RPCS.baseSepolia[0]
    ].filter(Boolean),
    backup: DEFAULT_RPCS.baseSepolia.slice(1)
  },
  optimism: {
    primary: [
      import.meta.env.VITE_OPTIMISM_MAINNET_RPC,
      DEFAULT_RPCS.optimism[0]
    ].filter(Boolean),
    backup: DEFAULT_RPCS.optimism.slice(1)
  },
  optimismSepolia: {
    primary: [
      import.meta.env.VITE_OPTIMISM_SEPOLIA_RPC,
      DEFAULT_RPCS.optimismSepolia[0]
    ].filter(Boolean),
    backup: DEFAULT_RPCS.optimismSepolia.slice(1)
  },
  arbitrum: {
    primary: [
      import.meta.env.VITE_ARBITRUM_MAINNET_RPC,
      DEFAULT_RPCS.arbitrum[0]
    ].filter(Boolean),
    backup: DEFAULT_RPCS.arbitrum.slice(1)
  },
  arbitrumSepolia: {
    primary: [
      import.meta.env.VITE_ARBITRUM_SEPOLIA_RPC,
      DEFAULT_RPCS.arbitrumSepolia[0]
    ].filter(Boolean),
    backup: DEFAULT_RPCS.arbitrumSepolia.slice(1)
  },
  polygon: {
    primary: [
      import.meta.env.VITE_POLYGON_MAINNET_RPC,
      DEFAULT_RPCS.polygon[0]
    ].filter(Boolean),
    backup: DEFAULT_RPCS.polygon.slice(1)
  },
  polygonAmoy: {
    primary: [
      import.meta.env.VITE_POLYGON_AMOY_RPC,
      DEFAULT_RPCS.polygonAmoy[0]
    ].filter(Boolean),
    backup: DEFAULT_RPCS.polygonAmoy.slice(1)
  },
  sei: {
    primary: [
      import.meta.env.VITE_SEI_MAINNET_RPC,
      DEFAULT_RPCS.sei[0]
    ].filter(Boolean),
    backup: DEFAULT_RPCS.sei.slice(1)
  },
  seiTestnet: {
    primary: [
      import.meta.env.VITE_SEI_TESTNET_RPC,
      DEFAULT_RPCS.seiTestnet[0]
    ].filter(Boolean),
    backup: DEFAULT_RPCS.seiTestnet.slice(1)
  }
};

// Initialize the RPC manager with our configuration
// This is now only used as a fallback mechanism
rpcManager.setConfig(config);

// Simple function to get best RPC URL
export function getBestRpcUrl(network: NetworkType): string {
  const networkConfig = config[network];
  // Use first available primary RPC, fallback to first backup if no primary available
  return networkConfig.primary[0] || networkConfig.backup[0];
}

export const rpcConfig = config;
